import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  UserCircleIcon, 
  PencilIcon, 
  CameraIcon,
  StarIcon,
  FireIcon,
  TrophyIcon,
  GiftIcon,
  CalendarIcon,
  ClockIcon,
  AcademicCapIcon,
  ChartBarIcon,
  BookOpenIcon,
  HeartIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  SpeakerWaveIcon,
  HandRaisedIcon
} from '@heroicons/react/24/outline'
import { useAppSelector } from '../store'

const Profile: React.FC = () => {
  const { user } = useAppSelector(state => state.auth)
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    school: user?.school || '',
    grade: user?.grade || '',
    studyGoal: (user as any)?.studyGoal || ''
  })

  const handleSave = () => {
    // TODO: Implement profile update
    setIsEditing(false)
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
    return num.toString()
  }

  const getStudyStyleIcon = (style: string) => {
    switch (style) {
      case 'visual': return <EyeIcon className="w-5 h-5" />
      case 'auditory': return <SpeakerWaveIcon className="w-5 h-5" />
      case 'kinesthetic': return <HandRaisedIcon className="w-5 h-5" />
      case 'reading': return <BookOpenIcon className="w-5 h-5" />
      default: return <BookOpenIcon className="w-5 h-5" />
    }
  }

  const getStudyStyleName = (style: string) => {
    switch (style) {
      case 'visual': return 'Görsel Öğrenme'
      case 'auditory': return 'İşitsel Öğrenme'
      case 'kinesthetic': return 'Kinestetik Öğrenme'
      case 'reading': return 'Okuma/Yazma'
      default: return 'Belirtilmemiş'
    }
  }

  const stats = [
    { label: 'Toplam Çalışma', value: `${user?.stats?.totalStudyTime || 0} saat`, icon: ClockIcon, color: 'blue' },
    { label: 'Tamamlanan Ders', value: ((user?.stats as any)?.completedLessons || 0).toString(), icon: AcademicCapIcon, color: 'green' },
    { label: 'Doğru Cevap', value: `${user?.stats?.correctAnswers || 0}/${user?.stats?.totalQuestions || 0}`, icon: ChartBarIcon, color: 'purple' },
    { label: 'Günlük Seri', value: `${user?.stats?.currentStreak || 0} gün`, icon: CalendarIcon, color: 'orange' }
  ]

  if (!user) return null

  return (
    <div className="max-w-4xl mx-auto p-3 sm:p-4 lg:p-6 space-y-4 sm:space-y-6">
      {/* Profile Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden"
      >
        <div className="h-24 sm:h-32 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
        <div className="relative px-3 sm:px-6 pb-4 sm:pb-6">
          <div className="flex flex-col sm:flex-row sm:items-end space-y-4 sm:space-y-0 sm:space-x-6 -mt-12 sm:-mt-16">
            <div className="relative flex justify-center sm:justify-start">
              {user.avatar ? (
                <img 
                  src={user.avatar} 
                  alt={user.name}
                  className="w-24 h-24 sm:w-32 sm:h-32 rounded-full border-4 border-white dark:border-gray-800"
                />
              ) : (
                <div className="w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center border-4 border-white dark:border-gray-800">
                  <span className="text-2xl sm:text-4xl font-bold text-white">
                    {user.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <button className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 p-1.5 sm:p-2 bg-white dark:bg-gray-700 rounded-full shadow-lg hover:shadow-xl transition-shadow">
                <CameraIcon className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600 dark:text-gray-300" />
              </button>
            </div>
            
            <div className="flex-1 pt-2 sm:pt-4 text-center sm:text-left">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                    {user.name}
                  </h1>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                    {user.email}
                  </p>
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:space-x-4 mt-2">
                    <span className="px-2 sm:px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-xs sm:text-sm font-medium">
                      Seviye {user.level}
                    </span>
                    {user.school && (
                      <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                        {user.school} - {user.grade}
                      </span>
                    )}
                    {(user as any).targetExam && (
                      <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-xs font-medium">
                        {(user as any).targetExam}
                      </span>
                    )}
                  </div>
                </div>
                
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className="flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm sm:text-base"
                >
                  <PencilIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">{isEditing ? 'İptal' : 'Düzenle'}</span>
                  <span className="sm:hidden">{isEditing ? 'İptal' : 'Düzenle'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6"
      >
        {/* XP Card */}
        <div className="bg-gradient-to-r from-orange-400 to-red-500 rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-xs sm:text-sm">Deneyim Puanı</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold">{formatNumber(user.xp)}</p>
            </div>
            <FireIcon className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-orange-100" />
          </div>
        </div>

        {/* Coins Card */}
        <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-xs sm:text-sm">Coin</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold">{formatNumber(user.coins || 0)}</p>
            </div>
            <GiftIcon className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-yellow-100" />
          </div>
        </div>

        {/* Badges Card */}
        <div className="bg-gradient-to-r from-green-400 to-blue-500 rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-xs sm:text-sm">Rozet</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold">{user.badges?.length || 0}</p>
            </div>
            <TrophyIcon className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-green-100" />
          </div>
        </div>

        {/* Level Card */}
        <div className="bg-gradient-to-r from-purple-400 to-pink-500 rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-xs sm:text-sm">Seviye</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold">{user.level}</p>
            </div>
            <StarIcon className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-purple-100" />
          </div>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6"
      >
        {stats.map((stat, index) => (
          <div key={index} className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">{stat.label}</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
              </div>
              <stat.icon className={`w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-${stat.color}-500`} />
            </div>
          </div>
        ))}
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Profile Information */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6"
        >
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">
            Profil Bilgileri
          </h2>
          
          {isEditing ? (
            <div className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                  Ad Soyad
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                  E-posta
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                  Okul
                </label>
                <input
                  type="text"
                  value={formData.school}
                  onChange={(e) => setFormData({ ...formData, school: e.target.value })}
                  className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                  Çalışma Hedefi
                </label>
                <textarea
                  value={formData.studyGoal}
                  onChange={(e) => setFormData({ ...formData, studyGoal: e.target.value })}
                  rows={3}
                  className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              
              <div className="flex gap-2 sm:gap-3">
                <button
                  onClick={handleSave}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-1.5 sm:py-2 px-3 sm:px-4 text-sm sm:text-base rounded-lg transition-colors"
                >
                  Kaydet
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-1.5 sm:py-2 px-3 sm:px-4 text-sm sm:text-base rounded-lg transition-colors"
                >
                  İptal
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              <div className="flex justify-between">
                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Okul:</span>
                <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white">
                  {user.school || 'Belirtilmemiş'}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Sınıf:</span>
                <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white">
                  {user.grade || 'Belirtilmemiş'}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Hedef Sınav:</span>
                <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white">
                  {(user as any).targetExam || 'Belirtilmemiş'}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Günlük Çalışma:</span>
                <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white">
                  {(user as any).dailyStudyHours || 0} saat
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Öğrenme Stili:</span>
                <div className="flex items-center space-x-1 sm:space-x-2">
                  {getStudyStyleIcon((user as any).studyStyle || '')}
                  <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white">
                    {getStudyStyleName((user as any).studyStyle || '')}
                  </span>
                </div>
              </div>

              {(user as any).studyGoal && (
                <div>
                  <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 block mb-1.5 sm:mb-2">Çalışma Hedefi:</span>
                  <p className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-2 sm:p-3 rounded-lg">
                    {(user as any).studyGoal}
                  </p>
                </div>
              )}

              <div className="flex justify-between">
                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Katılım Tarihi:</span>
                <span className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white">
                  {(user as any).joinDate ? new Date((user as any).joinDate).toLocaleDateString('tr-TR') : 'Belirtilmemiş'}
                </span>
              </div>
            </div>
          )}
        </motion.div>

        {/* Study Preferences */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6"
        >
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">
            Ders Tercihleri
          </h2>
          
          <div className="space-y-4 sm:space-y-6">
            {/* Favorite Subjects */}
            <div>
              <div className="flex items-center space-x-1.5 sm:space-x-2 mb-2 sm:mb-3">
                <HeartIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                <h3 className="font-medium text-sm sm:text-base text-gray-900 dark:text-white">Sevdiğim Dersler</h3>
              </div>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {(user as any).favoriteSubjects && (user as any).favoriteSubjects.length > 0 ? (
                  (user as any).favoriteSubjects.map((subject, index) => (
                    <span
                      key={index}
                      className="px-2 sm:px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-xs sm:text-sm"
                    >
                      {subject}
                    </span>
                  ))
                ) : (
                  <span className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm">Belirtilmemiş</span>
                )}
              </div>
            </div>

            {/* Weak Subjects */}
            <div>
              <div className="flex items-center space-x-1.5 sm:space-x-2 mb-2 sm:mb-3">
                <ExclamationTriangleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
                <h3 className="font-medium text-sm sm:text-base text-gray-900 dark:text-white">Zorlandığım Dersler</h3>
              </div>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {(user as any).weakSubjects && (user as any).weakSubjects.length > 0 ? (
                  (user as any).weakSubjects.map((subject, index) => (
                    <span
                      key={index}
                      className="px-2 sm:px-3 py-1 bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 rounded-full text-xs sm:text-sm"
                    >
                      {subject}
                    </span>
                  ))
                ) : (
                  <span className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm">Belirtilmemiş</span>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Badges Section */}
      {user.badges && user.badges.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6"
        >
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">
            Kazanılan Rozetler
          </h2>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {user.badges.map((badge, index) => (
              <div
                key={index}
                className="flex flex-col items-center p-3 sm:p-4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg sm:rounded-xl text-white"
              >
                <TrophyIcon className="w-6 h-6 sm:w-8 sm:h-8 mb-1.5 sm:mb-2" />
                <span className="text-xs sm:text-sm font-medium text-center">{badge}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  )
}

export default Profile